import {
  Radio,
  Input,
  Show,
  RadioSize,
  TruncateWithTooltip
} from "shared/components";
import { cx, formatToCurrency } from "shared/utils";
import { CriteriaRentType, Money } from "shared/types";
import { useAuthContext } from "providers/Auth";
import { Dispatch, SetStateAction } from "react";
import { PreviewApplicationRentByUnit } from "types/application";

type RentOption = "use_current" | "use_listed" | "manual";

interface SetRentAmountOptionsProps {
  currentApplicationRents: RentAmountData & {
    housing_assistance_voucher_amount?: Money;
    pet_rent_amount?: Money;
  };
  currentCriteriaRentType: CriteriaRentType;
  unitName: string;
  rentChangeType: RentOption;
  setRentChangeType: (rentChangeType: RentOption) => void;
  manualRent?: RentAmountData;
  setManualRent: Dispatch<SetStateAction<RentAmountData | undefined>>;
  listedApplicationRentByUnit?: PreviewApplicationRentByUnit;
}

export interface RentAmountData {
  monthly_rent_amount?: Money;
  rent_amount?: Money;
  home_rent_amount?: Money;
  site_rent_amount?: Money;
}

interface RentOptionItemProps {
  option: RentOption;
  label: string;
  rentAmount: RentAmountData & {
    housing_assistance_voucher_amount?: Money;
    pet_rent_amount?: Money;
  };
  description?: string;
  isChecked: boolean;
  onChange: (option: RentOption) => void;
  criteriaRentType: CriteriaRentType;
  disabled?: boolean;
}

const RentOptionItem = ({
  option,
  label,
  rentAmount,
  description,
  isChecked,
  onChange,
  criteriaRentType,
  disabled
}: RentOptionItemProps) => {
  const { pms, type } = useAuthContext();
  const homeSiteRentEnabled = pms.isManageAmerica || type.isHomeSiteRentEnabled;

  return (
    <div className="p-6 mb-4 border border-alpha-100 rounded-md bg-white">
      <div className="flex items-center justify-between">
        <Radio
          id={`rent-option-${option}`}
          checked={isChecked}
          onChange={() => onChange(option)}
          label={label}
          subLabelText={description}
          name="rent-option"
          size={RadioSize.LG}
          disabled={disabled}
        />
        <div className="flex items-center gap-4 text-sm text-content-300">
          {homeSiteRentEnabled ? (
            <>
              <Show if={criteriaRentType !== "site"}>
                <div>
                  <div className="space-y-2">
                    <p className="text-xs">Home rent</p>
                    <p className="font-medium">
                      {formatToCurrency(rentAmount.home_rent_amount || null)}
                    </p>
                  </div>
                </div>
              </Show>
              <Show if={criteriaRentType === "both"}>
                <span className="text-content-1000 font-medium">+</span>
              </Show>
              <Show if={criteriaRentType !== "home"}>
                <div className="space-y-2">
                  <p className="text-xs">Site rent</p>
                  <p className="font-medium">
                    {formatToCurrency(rentAmount.site_rent_amount || null)}
                  </p>
                </div>
              </Show>
            </>
          ) : (
            <div className="space-y-2">
              <p className="text-xs">Rent amount</p>
              <p className="font-medium">
                {formatToCurrency(rentAmount.rent_amount || null)}
              </p>
            </div>
          )}

          <span className="text-content-1000 font-medium">-</span>
          <div className="space-y-2 min-w-[60px]">
            <p className="text-xs">Assistance</p>
            <p className="font-medium">
              {formatToCurrency({
                cents: rentAmount.housing_assistance_voucher_amount?.cents || 0,
                currency: "USD"
              })}
            </p>
          </div>

          <span className="text-content-1000 font-medium">+</span>
          <div className="space-y-2 min-w-[35px]">
            <p className="text-xs">Pet</p>
            <p className="font-medium">
              {formatToCurrency({
                cents: rentAmount.pet_rent_amount?.cents || 0,
                currency: "USD"
              })}
            </p>
          </div>

          <span className="text-content-1000 font-medium">=</span>
          <div className="space-y-2 min-w-[53px] text-right">
            <p className="text-xs">Total rent</p>
            <p className="text-content-1000 font-medium">
              {formatToCurrency(rentAmount.monthly_rent_amount || null)}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

interface ManualRentInputProps {
  isChecked: boolean;
  onChange: (option: RentOption) => void;
  manualRent?: RentAmountData;
  onManualRentChange: (field: keyof RentAmountData, value: string) => void;
  calculateManualTotalRentCents: () => number;
  listedApplicationRentByUnit?: PreviewApplicationRentByUnit;
}

const ManualRentInput = ({
  isChecked,
  onChange,
  manualRent,
  onManualRentChange,
  calculateManualTotalRentCents,
  listedApplicationRentByUnit
}: ManualRentInputProps) => {
  const { pms, type } = useAuthContext();
  const homeSiteRentEnabled = pms.isManageAmerica || type.isHomeSiteRentEnabled;

  return (
    <div className="p-6 border border-alpha-100 rounded-md bg-white">
      <div className="mb-6">
        <Radio
          id="rent-option-manual"
          checked={isChecked}
          onChange={() => onChange("manual")}
          label="Set rent amount manually"
          name="rent-option"
          size={RadioSize.LG}
        />
      </div>
      <div className="flex items-center gap-4 text-sm">
        <div className="flex gap-4 items-center">
          {homeSiteRentEnabled ? (
            <>
              <Show
                if={
                  listedApplicationRentByUnit?.criteria_rent_type !==
                  CriteriaRentType.Site
                }
              >
                <div>
                  <Input
                    id="home-rent-manual-input"
                    className="w-[215px]"
                    type="money"
                    value={
                      manualRent?.home_rent_amount?.cents !== undefined
                        ? (manualRent.home_rent_amount.cents || 0) / 100
                        : ""
                    }
                    onChange={(e) =>
                      onManualRentChange("home_rent_amount", e.target.value)
                    }
                    min={0}
                    max={99999}
                    label="Home rent"
                    required
                    readOnly={!isChecked}
                    hideErrorMessage
                  />
                </div>
              </Show>
              <Show
                if={
                  listedApplicationRentByUnit?.criteria_rent_type ===
                  CriteriaRentType.Both
                }
              >
                <span className="text-content-1000 font-medium pt-6">+</span>
              </Show>
              <Show
                if={
                  listedApplicationRentByUnit?.criteria_rent_type !==
                  CriteriaRentType.Home
                }
              >
                <div>
                  <Input
                    id="site-rent-manual-input"
                    className="w-[215px]"
                    type="money"
                    value={
                      manualRent?.site_rent_amount?.cents !== undefined
                        ? (manualRent.site_rent_amount.cents || 0) / 100
                        : ""
                    }
                    onChange={(e) =>
                      onManualRentChange("site_rent_amount", e.target.value)
                    }
                    min={0}
                    max={99999}
                    label="Site rent"
                    required
                    readOnly={!isChecked}
                    hideErrorMessage
                  />
                </div>
              </Show>
            </>
          ) : (
            <div>
              <Input
                id="application-rent-manual-input"
                className="!w-[218px]"
                type="money"
                value={
                  manualRent?.rent_amount?.cents !== undefined
                    ? (manualRent.rent_amount.cents || 0) / 100
                    : ""
                }
                min={0}
                max={99999}
                onChange={(e) =>
                  onManualRentChange("rent_amount", e.target.value)
                }
                label="Rent amount"
                required
                readOnly={!isChecked}
                hideErrorMessage
              />
            </div>
          )}
        </div>
        <div className="flex-1 flex gap-4 items-center justify-between pt-6">
          <span className="text-content-1000 font-medium">-</span>
          <div
            className={cx(
              "space-y-2 text-content-300",
              homeSiteRentEnabled ? "min-w-[60px]" : "min-w-[85px]"
            )}
          >
            <p className="text-xs">Assistance</p>
            <p className="font-medium">
              {formatToCurrency(
                listedApplicationRentByUnit?.housing_assistance_voucher_amount ||
                  null
              )}
            </p>
          </div>
          <span className="text-content-1000 font-medium">+</span>
          <div
            className={cx(
              "space-y-2 text-content-300",
              homeSiteRentEnabled ? "min-w-[35px]" : "min-w-[85px]"
            )}
          >
            <p className="text-xs">Pet</p>
            <p className="font-medium">
              {formatToCurrency(
                listedApplicationRentByUnit?.pet_rent_amount || null
              )}
            </p>
          </div>
          <span className="text-content-1000 font-medium">=</span>
          <div
            className={cx(
              "space-y-2",
              homeSiteRentEnabled ? "min-w-[60px]" : "min-w-[90px]"
            )}
          >
            <p className="text-xs text-content-300">Application rent</p>
            <p className="text-content-1000 font-medium text-end">
              <TruncateWithTooltip
                content={formatToCurrency({
                  cents: calculateManualTotalRentCents(),
                  currency: "USD"
                })}
                maxWidth="90px"
                tooltipClassName="z-[9999]"
              />
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export const SetRentAmountOptions = ({
  unitName,
  currentApplicationRents,
  rentChangeType,
  setRentChangeType,
  manualRent,
  setManualRent,
  currentCriteriaRentType,
  listedApplicationRentByUnit
}: SetRentAmountOptionsProps) => {
  const { pms, permissions, type } = useAuthContext();
  const homeSiteRentEnabled = pms.isManageAmerica || type.isHomeSiteRentEnabled;

  const calculateManualTotalRentCents = () => {
    const baseRent = homeSiteRentEnabled
      ? (manualRent?.home_rent_amount?.cents || 0) +
        (manualRent?.site_rent_amount?.cents || 0)
      : manualRent?.rent_amount?.cents || 0;
    const petRent = listedApplicationRentByUnit?.pet_rent_amount?.cents || 0;
    const assistanceRent =
      listedApplicationRentByUnit?.housing_assistance_voucher_amount?.cents ||
      0;
    const result = baseRent + petRent - assistanceRent;
    if (result < 0) return 0;
    return result;
  };

  const handleManualRentChange = (
    field: keyof RentAmountData,
    value: string
  ) => {
    if (value === "") {
      setManualRent((prev) => ({
        ...prev,
        [field]: undefined
      }));
      return;
    }
    const cents = parseFloat(value) * 100 || 0;
    setManualRent((prev) => ({
      ...prev,
      [field]: { cents, currency: "USD" }
    }));
  };

  return (
    <div className="px-6 py-8 bg-background-900 max-h-[500px] overflow-y-auto">
      <RentOptionItem
        option="use_current"
        label="Use current rent"
        rentAmount={currentApplicationRents}
        isChecked={rentChangeType === "use_current"}
        onChange={setRentChangeType}
        criteriaRentType={currentCriteriaRentType}
        disabled={
          currentCriteriaRentType !==
          (listedApplicationRentByUnit?.criteria_rent_type ||
            CriteriaRentType.Both)
        }
      />

      <Show if={permissions.canEditApplicationRent}>
        <RentOptionItem
          option="use_listed"
          label="Use listed rent"
          rentAmount={listedApplicationRentByUnit || {}}
          description={`For Unit ${unitName}`}
          isChecked={rentChangeType === "use_listed"}
          onChange={setRentChangeType}
          criteriaRentType={
            listedApplicationRentByUnit?.criteria_rent_type ||
            CriteriaRentType.Both
          }
        />
      </Show>

      <Show if={permissions.canEditApplicationRent}>
        <ManualRentInput
          isChecked={rentChangeType === "manual"}
          onChange={setRentChangeType}
          manualRent={manualRent}
          onManualRentChange={handleManualRentChange}
          calculateManualTotalRentCents={calculateManualTotalRentCents}
          listedApplicationRentByUnit={listedApplicationRentByUnit}
        />
      </Show>
    </div>
  );
};
